import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import venueDashboardApiService from '../../../services/venueDashboardApi';
import discountCodeApiService from '../../../services/discountCodeApi';
import { resolveStoredHostPhotoUrl } from '../../../utils/hostPhotoUrl';
import {
  getCachedVenueDashboardBundle,
  setCachedVenueDashboardBundle,
  clearVenueDashboardCache
} from '../../../utils/venueDashboardCache';
import { clearVenueRecordCache } from '../../../utils/venueRecordCache';
import { clearAvailabilityCache } from '../../../utils/availabilityCache';
import { touchVenueDashboardActivity } from '../../../utils/venueDashboardSession';
import { getBackendUserId } from '../../../utils/messageThreadDisplay';
import { applyVenueContactOverrides } from '../../../utils/venueContactOverrides';
import { CALENDAR_API_BASE_URL } from '../../../config/apiEndpoints';
import { hostRulesToFormText, extractHostRulesFromEventRules } from '../../../utils/bookingHostRules';
import { normalizeTagSlugs } from '../../../utils/catalogTags';
import {
  isVenueDashboardBundleEnabled,
  mapDashboardBundleResponse
} from '../../../utils/venueDashboardBundleApi';

const normalizeVenueCatalogBundle = (addons = [], others = [], packagesData = []) => ({
  addOnItems: (Array.isArray(addons) ? addons : []).map((item) => ({
    ...item,
    tags: normalizeTagSlugs(item?.tags),
  })),
  otherItems: (Array.isArray(others) ? others : []).map((item) => ({
    ...item,
    tags: normalizeTagSlugs(item?.tags),
  })),
  packages: (Array.isArray(packagesData) ? packagesData : []).map((pkg) => ({
    ...pkg,
    tags: normalizeTagSlugs(pkg?.tags),
  })),
});

const filterDiscountCodesForVenueOwner = (codes, ownerUserId) => {
  if (!Array.isArray(codes)) return [];
  if (!ownerUserId) return codes;
  return codes.filter(
    (code) =>
      !code?.created_by_user_id ||
      String(code.created_by_user_id) === String(ownerUserId)
  );
};

const VENUE_TYPE_MAP = {
  cafe: 'cafes', café: 'cafes', cafè: 'cafes',
  cafes: 'cafes',
  conference_room: 'conference_rooms', 'conference room': 'conference_rooms', conference_rooms: 'conference_rooms',
  banquet_hall: 'banquet_halls', 'banquet hall': 'banquet_halls', banquet_halls: 'banquet_halls',
  outdoor_space: 'outdoor_spaces', 'outdoor space': 'outdoor_spaces', outdoor_spaces: 'outdoor_spaces',
  studio: 'studios', studios: 'studios',
  loft: 'lofts', lofts: 'lofts',
  hotel: 'hotels', hotels: 'hotels',
  restaurant: 'restaurants', restaurants: 'restaurants',
  auditorium: 'auditoriums', auditoriums: 'auditoriums',
  venue: 'venue'
};
const normalizeVenueType = (v) => {
  if (!v) return '';
  const key = String(v).toLowerCase().trim();
  return VENUE_TYPE_MAP[key] || key.replace(/\s+/g, '_') || '';
};

const buildOwnerSettingsFromProfile = (userProfile, currentUser) => {
  if (!userProfile) return null;
  return {
    name: [userProfile.first_name, userProfile.last_name].filter(Boolean).join(' ') ||
      userProfile.display_name || userProfile.name || currentUser?.displayName || '',
    email: currentUser?.email || userProfile.email || '',
    phone: userProfile.phone || ''
  };
};

const buildOwnerSettingsFromVenue = (venueData, userProfile, currentUser) => {
  if (!venueData) return buildOwnerSettingsFromProfile(userProfile, currentUser);
  const normalizedVenue = applyVenueContactOverrides(venueData);
  const ownerFirstName = normalizedVenue?.owner_first_name ?? normalizedVenue?.owner?.first_name ?? '';
  const ownerLastName = normalizedVenue?.owner_last_name ?? normalizedVenue?.owner?.last_name ?? '';
  const ownerEmail = normalizedVenue?.contact_email ?? '';
  const ownerPhone = normalizedVenue?.contact_phone ?? '';
  const ownerName =
    normalizedVenue?.contact_name ||
    [ownerFirstName, ownerLastName].filter(Boolean).join(' ') ||
    '';
  if (ownerName || ownerEmail || ownerPhone) {
    return { name: ownerName, email: ownerEmail, phone: ownerPhone };
  }
  return buildOwnerSettingsFromProfile(userProfile, currentUser);
};

const venueHasStoredContact = (venueData) => {
  if (!venueData) return false;
  const normalizedVenue = applyVenueContactOverrides(venueData);
  return Boolean(
    normalizedVenue?.contact_name ||
    normalizedVenue?.contact_email ||
    normalizedVenue?.contact_phone
  );
};

const enrichOwnerSettingsFromUserApi = async (venueData, partialSettings) => {
  const normalizedVenue = applyVenueContactOverrides(venueData);
  const ownerId = normalizedVenue?.owner_id ?? normalizedVenue?.owner?.id;
  if (!ownerId) return partialSettings;

  const ownerUser = await venueDashboardApiService.getUserById(ownerId);
  if (!ownerUser) return partialSettings;

  const ownerFirstName =
    normalizedVenue?.owner_first_name ??
    normalizedVenue?.owner?.first_name ??
    ownerUser.first_name ??
    ownerUser.firstName ??
    '';
  const ownerLastName =
    normalizedVenue?.owner_last_name ??
    normalizedVenue?.owner?.last_name ??
    ownerUser.last_name ??
    ownerUser.lastName ??
    '';
  const ownerEmail =
    normalizedVenue?.contact_email || ownerUser.email || partialSettings?.email || '';
  const ownerPhone =
    normalizedVenue?.contact_phone || ownerUser.phone || partialSettings?.phone || '';
  const ownerName =
    normalizedVenue?.contact_name ||
    [ownerFirstName, ownerLastName].filter(Boolean).join(' ') ||
    partialSettings?.name ||
    '';

  return { name: ownerName, email: ownerEmail, phone: ownerPhone };
};

/**
 * Custom hook for managing venue dashboard data
 */
export const useVenueDashboardData = (venueId) => {
  const { currentUser, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardRefreshing, setDashboardRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  // Settings state
  const [operatingHours, setOperatingHours] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [dynamicPricing, setDynamicPricing] = useState([]);
  const [defaultPrice, setDefaultPrice] = useState(200);
  const [loadedDefaultPrice, setLoadedDefaultPrice] = useState(null);
  const [defaultPerPersonPrice, setDefaultPerPersonPrice] = useState(25);
  const [loadedDefaultPerPersonPrice, setLoadedDefaultPerPersonPrice] = useState(null);
  const [dynamicPricingPerPerson, setDynamicPricingPerPerson] = useState([]);
  const [addOnItems, setAddOnItems] = useState([]);
  const [otherItems, setOtherItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [venueSpaces, setVenueSpaces] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [primarySpaceId, setPrimarySpaceId] = useState(null);
  const [flatPricing, setFlatPricing] = useState([]);
  const [bookingRules, setBookingRules] = useState(null);
  const [operatingHoursPerPerson, setOperatingHoursPerPerson] = useState({});
  
  // Venue details state
  const [venueDetails, setVenueDetails] = useState({
    name: '',
    address: '',
    description: '',
    max_capacity: 0,
    image_url: ''
  });
  
  // Account settings state (venue owner details)
  const [accountSettings, setAccountSettings] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [ownerAccountSettings, setOwnerAccountSettings] = useState({
    name: '',
    email: '',
    phone: ''
  });
  
  // Busy times state
  const [busyTimes, setBusyTimes] = useState([]);
  
  // Get venue info from user profile (fallback)
  const currentVenue = userProfile?.owned_venues?.find(v => v.id === venueId);
  const venueName = venueDetails?.name || currentVenue?.name || 'Your Venue';
  
  /** Last-synced busy blocks from Occasia API (no Google pull). Used on dashboard load. */
  const loadStoredBusyTimes = useCallback(async () => {
    if (!venueId) {
      setBusyTimes([]);
      return [];
    }
    try {
      const list = await venueDashboardApiService.getBusyTimes(venueId);
      const mapped = (Array.isArray(list) ? list : []).map((bt) => ({
        ...bt,
        start_time: bt.start_time || bt.start || bt.startTime,
        end_time: bt.end_time || bt.end || bt.endTime,
        calendar_name: bt.calendar_name || bt.calendarName || 'Calendar'
      }));
      setBusyTimes(mapped);
      return mapped;
    } catch {
      setBusyTimes([]);
      return [];
    }
  }, [venueId]);

  /** Fetches fresh busy times from Google via calendar Lambda — call only from “Sync calendar”. */
  const fetchBusyTimes = async (startDate = null, endDate = null) => {
    if (!venueId) {
      setBusyTimes([]);
      return [];
    }
    
    try {
      const params = new URLSearchParams();
      if (startDate) {
        params.append('start', startDate.toISOString());
      }
      if (endDate) {
        params.append('end', endDate.toISOString());
      }
      
      if (!startDate && !endDate) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const nextMonth = new Date(today);
        nextMonth.setDate(nextMonth.getDate() + 30);
        params.append('start', today.toISOString());
        params.append('end', nextMonth.toISOString());
      }
      
      // Get owner email for the calendar/busy endpoint
      // Try multiple sources: venue details, current venue from profile, user profile, current user
      const ownerEmail = venueDetails?.contact_email || 
                        currentVenue?.contact_email || 
                        userProfile?.email || 
                        currentUser?.email;
      
      if (!ownerEmail) {
        setBusyTimes([]);
        return [];
      }
      
      // Build query parameters for /calendar/busy endpoint
      const queryParams = new URLSearchParams();
      queryParams.append('user_id', ownerEmail);
      queryParams.append('venue_id', venueId);
      queryParams.append('provider', 'google');
      
      if (startDate && endDate) {
        // Calculate days between dates
        const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
        queryParams.append('days', days.toString());
      } else {
        queryParams.append('days', '30'); // Default to 30 days
      }
      
      const queryString = queryParams.toString();
      const url = `${CALENDAR_API_BASE_URL}/calendar/busy?${queryString}`;

      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        // Don't throw for 404 - just return empty array (venue might not have busy times configured)
        if (response.status === 404) {
          setBusyTimes([]);
          return [];
        }
        
        throw new Error(`HTTP_${response.status}`);
      }
      
      const data = await response.json();

      // The /calendar/busy endpoint might return data in different formats
      let busyTimesList = [];
      if (Array.isArray(data)) {
        busyTimesList = data;
      } else if (data.busy_times && Array.isArray(data.busy_times)) {
        busyTimesList = data.busy_times;
      } else if (data.success && data.busy_times && Array.isArray(data.busy_times)) {
        busyTimesList = data.busy_times;
      } else if (data && typeof data === 'object') {
        // Try to extract busy times from response object
        busyTimesList = Object.values(data).filter(item => 
          item && (item.start_time || item.start || item.startTime)
        );
      }

      // Map response to expected format
      const mappedBusyTimes = busyTimesList.map(bt => ({
        ...bt,
        start_time: bt.start_time || bt.start || bt.startTime,
        end_time: bt.end_time || bt.end || bt.endTime,
        calendar_name: bt.calendar_name || bt.calendarName || 'Calendar'
      }));
      
      setBusyTimes(mappedBusyTimes);
      return mappedBusyTimes;
    } catch (error) {
      setBusyTimes([]);
      return [];
    }
  };
  
  const applyOwnerSettings = useCallback((ownerSettings) => {
    if (!ownerSettings) return;
    setAccountSettings(ownerSettings);
    setOwnerAccountSettings(ownerSettings);
  }, []);

  const resolveOwnerSettingsInBackground = useCallback(async (venueData, cachedOwnerSettings) => {
    if (cachedOwnerSettings) {
      applyOwnerSettings(cachedOwnerSettings);
      return;
    }
    if (!venueData) {
      applyOwnerSettings(buildOwnerSettingsFromProfile(userProfile, currentUser));
      return;
    }
    if (venueHasStoredContact(venueData)) {
      applyOwnerSettings(buildOwnerSettingsFromVenue(venueData, userProfile, currentUser));
      return;
    }
    try {
      const partial = buildOwnerSettingsFromVenue(venueData, userProfile, currentUser);
      const enriched = await enrichOwnerSettingsFromUserApi(venueData, partial);
      applyOwnerSettings(enriched || partial);
    } catch {
      applyOwnerSettings(buildOwnerSettingsFromVenue(venueData, userProfile, currentUser));
    }
  }, [applyOwnerSettings, currentUser, userProfile]);

  const fetchCatalogItemsAndApply = useCallback(async (targetVenueId) => {
    if (!targetVenueId) return null;
    try {
      const [addons, others, packagesData] = await Promise.all([
        venueDashboardApiService.getVenueAddOnItems(targetVenueId, { itemKind: 'addon' }),
        venueDashboardApiService.getVenueAddOnItems(targetVenueId, { itemKind: 'other' }),
        venueDashboardApiService.getVenuePackages(targetVenueId),
      ]);
      const catalog = normalizeVenueCatalogBundle(addons, others, packagesData);
      setAddOnItems((prev) => (
        catalog.addOnItems.length === 0 && prev.length > 0 ? prev : catalog.addOnItems
      ));
      setOtherItems((prev) => (
        catalog.otherItems.length === 0 && prev.length > 0 ? prev : catalog.otherItems
      ));
      setPackages((prev) => (
        catalog.packages.length === 0 && prev.length > 0 ? prev : catalog.packages
      ));
      return catalog;
    } catch {
      return null;
    }
  }, []);

  // Load dashboard data (cached for 10 minutes; use refreshDashboardData to force reload)
  const loadDashboardData = useCallback(async ({ forceRefresh = false } = {}) => {
    touchVenueDashboardActivity();
    const applyBundleState = (bundle) => {
      const {
        hours,
        holidayData,
        pricingData,
        defaultPerPersonData,
        dynamicPerPersonData,
        venueData,
        addOnItemsData,
        otherItemsData,
        packagesData,
        spacesData,
        discountCodesData,
        flatPricingData,
        bookingRulesData,
        perPersonHoursData,
        ownerSettings: cachedOwnerSettings
      } = bundle;

      setOperatingHours(hours);
      setHolidays(holidayData);
      setDynamicPricing(pricingData);
      if (defaultPerPersonData?.default_per_person_price != null) {
        setDefaultPerPersonPrice(defaultPerPersonData.default_per_person_price);
        setLoadedDefaultPerPersonPrice(defaultPerPersonData.default_per_person_price);
      }
      setDynamicPricingPerPerson(Array.isArray(dynamicPerPersonData) ? dynamicPerPersonData : []);
      const catalog = normalizeVenueCatalogBundle(addOnItemsData, otherItemsData, packagesData);
      setAddOnItems(catalog.addOnItems);
      setOtherItems(catalog.otherItems);
      setPackages(catalog.packages);
      setVenueSpaces(spacesData);
      setDiscountCodes(discountCodesData);
      setPrimarySpaceId(spacesData.length > 0 ? spacesData[0].id : null);
      setFlatPricing(flatPricingData);
      setBookingRules(bookingRulesData);
      setOperatingHoursPerPerson(Object.keys(perPersonHoursData).length > 0 ? perPersonHoursData : hours);

      if (venueData) {
        const primaryImage = venueData.primary_image_url ||
          (venueData.images && venueData.images.length > 0
            ? (venueData.images.find(img => img.is_primary)?.image_url || venueData.images[0]?.image_url)
            : null) ||
          venueData.image_url ||
          venueData.image ||
          '';
        const amenities = venueData.amenities || {};
        const foodBeverage = amenities.food_beverage || amenities.foodBeverage || {};
        const eventRules = amenities.event_rules || amenities.eventRules || {};
        const alcohol = foodBeverage.alcohol || {};
        const inHouseCateringOptions = Array.isArray(foodBeverage.inHouseCatering)
          ? foodBeverage.inHouseCatering
          : [];
        const externalCateringOptions = Array.isArray(foodBeverage.externalCatering)
          ? foodBeverage.externalCatering
          : [];
        const allowedEvents = Array.isArray(eventRules.allowedEvents)
          ? eventRules.allowedEvents
          : [];
        const hostRules = extractHostRulesFromEventRules(eventRules);
        const amenitiesList = Array.isArray(amenities.amenities)
          ? amenities.amenities
          : (Array.isArray(venueData.facilities) ? venueData.facilities : []);
        const toLines = (items, mapper) =>
          items
            .map((item) => mapper(item))
            .map((value) => (value == null ? '' : String(value).trim()))
            .filter(Boolean)
            .join('\n');
        const hostRaw =
          venueData.host_photo_url ||
          venueData.owner_photo_url ||
          venueData.contact_photo_url ||
          '';
        const hostResolved = resolveStoredHostPhotoUrl(hostRaw, {
          venueId,
          images: venueData.images
        });
        setVenueDetails({
          name: venueData.name || '',
          address: venueData.address || '',
          description: venueData.description || '',
          host_photo_url: hostResolved ? String(hostResolved).trim() : '',
          max_capacity: venueData.max_capacity || venueData.seating_capacity || 0,
          seating_capacity: venueData.seating_capacity ?? venueData.max_capacity ?? 0,
          standing_capacity: venueData.standing_capacity ?? 0,
          venue_type: normalizeVenueType(venueData.venue_type || venueData.type || ''),
          map_link: venueData.map_link || venueData.mapLink || venueData.google_maps_url || '',
          image_url: primaryImage,
          serves_alcohol: venueData.serves_alcohol ?? alcohol.venueProvides ?? false,
          outside_alcohol_allowed: venueData.outside_alcohol_allowed ?? alcohol.byoAllowed ?? false,
          corkage_fee_for_byo: alcohol.corkageFee ?? false,
          in_house_catering: venueData.in_house_catering_allowed ?? venueData.in_house_catering ?? (Array.isArray(foodBeverage.inHouseCatering) && foodBeverage.inHouseCatering.length > 0),
          external_catering_allowed: venueData.external_catering_allowed ?? (Array.isArray(foodBeverage.externalCatering) && foodBeverage.externalCatering.length > 0),
          amenities: venueData.amenities,
          facilities: venueData.facilities,
          stripe_payments_enabled: Boolean(venueData.stripe_payments_enabled),
          stripe_checkout_enabled:
            venueData.stripe_checkout_enabled != null
              ? Boolean(venueData.stripe_checkout_enabled)
              : null,
          in_house_catering_options_text: toLines(
            inHouseCateringOptions,
            (item) => (typeof item === 'string' ? item : (item?.name || item?.label || ''))
          ),
          external_catering_options_text: toLines(
            externalCateringOptions,
            (item) => (typeof item === 'string' ? item : (item?.name || item?.label || ''))
          ),
          allowed_events_text: toLines(
            allowedEvents,
            (item) => (typeof item === 'string' ? item : (item?.name || item?.label || ''))
          ),
          host_rules_text: hostRulesToFormText(hostRules),
          amenities_text: toLines(
            amenitiesList,
            (item) => (typeof item === 'string' ? item : (item?.name || item?.label || ''))
          ),
          cancellation_policy_text:
            eventRules.cancellationPolicy ||
            eventRules.cancellation_policy ||
            venueData.cancellation_policy ||
            '',
          payment_link: venueData.payment_link || venueData.payment_url || '',
          stripe_connect: venueData.stripe_connect || venueData.stripeConnect || null,
          stripe_account_id:
            venueData.stripe_account_id ||
            venueData.stripe_connect_account_id ||
            venueData.stripe_connect?.stripe_account_id ||
            null,
          stripe_onboarding_status:
            venueData.stripe_onboarding_status ||
            venueData.stripe_connect_status ||
            venueData.stripe_connect?.stripe_onboarding_status ||
            null,
          stripe_charges_enabled:
            venueData.stripe_charges_enabled ??
            venueData.charges_enabled ??
            venueData.stripe_connect?.charges_enabled ??
            null,
          stripe_payouts_enabled:
            venueData.stripe_payouts_enabled ??
            venueData.payouts_enabled ??
            venueData.stripe_connect?.payouts_enabled ??
            null,
          stripe_account_email:
            venueData.stripe_account_email ||
            venueData.stripe_connect?.stripe_account_email ||
            null
        });
      }

      applyOwnerSettings(cachedOwnerSettings);

      if (venueData?.hourly_rate != null) {
        setDefaultPrice(venueData.hourly_rate);
        setLoadedDefaultPrice(venueData.hourly_rate);
      }
    };

    const finishDashboardPaint = (bundle) => {
      applyBundleState(bundle);
      if (!bundle.ownerSettings) {
        void resolveOwnerSettingsInBackground(bundle.venueData, null);
      }
      void loadStoredBusyTimes();
    };

    try {
      setError(null);

      if (!venueId) {
        throw new Error('Venue ID is not available');
      }

      if (!forceRefresh) {
        const cached = getCachedVenueDashboardBundle(venueId);
        if (cached) {
          finishDashboardPaint(cached);
          setLoading(false);
          void fetchCatalogItemsAndApply(venueId).then((catalog) => {
            if (!catalog) return;
            const fetchedEmpty =
              catalog.addOnItems.length === 0
              && catalog.otherItems.length === 0
              && catalog.packages.length === 0;
            const cachedHadItems =
              (cached.addOnItemsData?.length ?? 0) > 0
              || (cached.otherItemsData?.length ?? 0) > 0
              || (cached.packagesData?.length ?? 0) > 0;
            if (fetchedEmpty && cachedHadItems) return;
            setCachedVenueDashboardBundle(venueId, {
              ...cached,
              addOnItemsData: catalog.addOnItems,
              otherItemsData: catalog.otherItems,
              packagesData: catalog.packages,
            });
          });
          return;
        }
        setLoading(true);
      } else {
        clearVenueDashboardCache(venueId);
        clearVenueRecordCache(venueId);
        clearAvailabilityCache(venueId);
      }

      if (isVenueDashboardBundleEnabled()) {
        try {
          const apiBundle = await venueDashboardApiService.getDashboardBundle(venueId);
          const mapped = mapDashboardBundleResponse(apiBundle, {
            transformOperatingHoursList: (rows) =>
              venueDashboardApiService.transformOperatingHoursList(rows)
          });
          if (mapped?.venueData) {
            const ownerUserId = getBackendUserId(currentUser, userProfile);
            const discountCodesRaw = await discountCodeApiService.listDiscountCodes({
              include_expired: true,
              limit: 100
            });
            const discountCodesData = filterDiscountCodesForVenueOwner(
              Array.isArray(discountCodesRaw) ? discountCodesRaw : (discountCodesRaw?.items ?? []),
              ownerUserId
            );
            const ownerSettings = buildOwnerSettingsFromVenue(mapped.venueData, userProfile, currentUser);
            const bundle = {
              ...mapped,
              discountCodesData,
              ownerSettings
            };
            finishDashboardPaint(bundle);
            setCachedVenueDashboardBundle(venueId, bundle);
            if (!venueHasStoredContact(mapped.venueData)) {
              void resolveOwnerSettingsInBackground(mapped.venueData, ownerSettings);
            }
            setLoading(false);
            return;
          }
        } catch {
          // Fall back to legacy parallel venue API calls.
        }
      }

      const results = await Promise.allSettled([
        venueDashboardApiService.getOperatingHours(venueId),
        venueDashboardApiService.getHolidays(venueId),
        venueDashboardApiService.getDynamicPricing(venueId),
        venueDashboardApiService.getDefaultPerPersonPrice(venueId),
        venueDashboardApiService.getDynamicPricingPerPerson(venueId),
        venueDashboardApiService.getVenueDetails(venueId, { forceRefresh }),
        Promise.all([
          venueDashboardApiService.getVenueAddOnItems(venueId, { itemKind: 'addon' }),
          venueDashboardApiService.getVenueAddOnItems(venueId, { itemKind: 'other' })
        ]).then(([addons, others]) => ({ addons, others })),
        venueDashboardApiService.getVenuePackages(venueId),
        venueDashboardApiService.getVenueSpaces(venueId),
        discountCodeApiService.listDiscountCodes({ include_expired: true, limit: 100 }),
        venueDashboardApiService.getFlatPricing(venueId),
        venueDashboardApiService.getBookingRules(venueId),
        venueDashboardApiService.getOperatingHoursPerPerson(venueId)
      ]);

      const hours = results[0].status === 'fulfilled' ? results[0].value : {};
      const holidayData = results[1].status === 'fulfilled' ? results[1].value : [];
      const pricingData = results[2].status === 'fulfilled' ? results[2].value : [];
      const defaultPerPersonData = results[3].status === 'fulfilled' ? results[3].value : null;
      const dynamicPerPersonData = results[4].status === 'fulfilled' ? results[4].value : [];
      const venueData = results[5].status === 'fulfilled' ? results[5].value : null;
      const venueItemsBundle = results[6].status === 'fulfilled' ? results[6].value : {};
      const catalogFromApi = normalizeVenueCatalogBundle(
        venueItemsBundle?.addons ?? [],
        venueItemsBundle?.others ?? [],
        results[7].status === 'fulfilled' ? results[7].value : []
      );
      const addOnItemsData = catalogFromApi.addOnItems;
      const otherItemsData = catalogFromApi.otherItems;
      const packagesData = catalogFromApi.packages;
      const spacesData = results[8].status === 'fulfilled' ? results[8].value : [];
      const discountCodesRaw = results[9].status === 'fulfilled' ? results[9].value : [];
      const flatPricingData = results[10].status === 'fulfilled' ? results[10].value : [];
      const bookingRulesData = results[11].status === 'fulfilled' ? results[11].value : null;
      const perPersonHoursData = results[12].status === 'fulfilled' ? results[12].value : {};
      const ownerUserId = getBackendUserId(currentUser, userProfile);
      const discountCodesData = filterDiscountCodesForVenueOwner(
        Array.isArray(discountCodesRaw) ? discountCodesRaw : (discountCodesRaw?.items ?? []),
        ownerUserId
      );

      let ownerSettings = buildOwnerSettingsFromVenue(venueData, userProfile, currentUser);
      if (!venueHasStoredContact(venueData) && venueData) {
        ownerSettings = await enrichOwnerSettingsFromUserApi(venueData, ownerSettings);
      }

      const bundle = {
        hours,
        holidayData,
        pricingData,
        defaultPerPersonData,
        dynamicPerPersonData,
        venueData,
        addOnItemsData,
        otherItemsData,
        packagesData,
        spacesData,
        discountCodesData,
        flatPricingData,
        bookingRulesData,
        perPersonHoursData,
        ownerSettings
      };

      finishDashboardPaint(bundle);
      setCachedVenueDashboardBundle(venueId, bundle);
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('401') || msg.includes('403')) {
        setError('You are not authorised to view this dashboard. Please log in again.');
      } else if (msg.includes('404')) {
        setError('Venue not found. Please check the URL and try again.');
      } else if (msg.includes('500') || msg.includes('502') || msg.includes('503')) {
        setError('The server is temporarily unavailable. Please try again in a moment.');
      } else if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
        setError('Unable to reach the server. Please check your connection.');
      } else {
        setError('Failed to load dashboard. Please refresh the page or try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, [venueId, userProfile, currentUser, applyOwnerSettings, resolveOwnerSettingsInBackground, fetchCatalogItemsAndApply, loadStoredBusyTimes]);

  const refreshDashboardData = useCallback(async () => {
    setDashboardRefreshing(true);
    try {
      await loadDashboardData({ forceRefresh: true });
    } finally {
      setDashboardRefreshing(false);
    }
  }, [loadDashboardData]);

  const refreshDiscountCodes = useCallback(async () => {
    if (!venueId) return [];
    const raw = await discountCodeApiService.listDiscountCodes({ include_expired: true, limit: 100 });
    const ownerUserId = getBackendUserId(currentUser, userProfile);
    const filtered = filterDiscountCodesForVenueOwner(
      Array.isArray(raw) ? raw : (raw?.items ?? []),
      ownerUserId
    );
    setDiscountCodes(filtered);
    return filtered;
  }, [venueId, currentUser, userProfile]);

  const createVenueDiscountCode = useCallback(
    async (payload) => {
      const ownerUserId = getBackendUserId(currentUser, userProfile);
      const created = await discountCodeApiService.createDiscountCode({
        ...payload,
        created_by_user_id: ownerUserId || payload?.created_by_user_id || null
      });
      await refreshDiscountCodes();
      return created;
    },
    [currentUser, userProfile, refreshDiscountCodes]
  );

  const deleteVenueDiscountCode = useCallback(
    async (codeId) => {
      await discountCodeApiService.deleteDiscountCode(codeId);
      await refreshDiscountCodes();
    },
    [refreshDiscountCodes]
  );

  useEffect(() => {
    if (venueId) {
      loadDashboardData();
    } else {
      setLoading(false);
      setError('No venue ID provided');
    }
  }, [venueId, loadDashboardData]);
  
  return {
    loading,
    dashboardRefreshing,
    refreshDashboardData,
    error,
    venueName,
    operatingHours,
    setOperatingHours,
    holidays,
    setHolidays,
    dynamicPricing,
    setDynamicPricing,
    defaultPrice,
    setDefaultPrice,
    loadedDefaultPrice,
    defaultPerPersonPrice,
    setDefaultPerPersonPrice,
    loadedDefaultPerPersonPrice,
    dynamicPricingPerPerson,
    setDynamicPricingPerPerson,
    venueDetails,
    setVenueDetails,
    addOnItems,
    setAddOnItems,
    otherItems,
    setOtherItems,
    packages,
    setPackages,
    venueSpaces,
    primarySpaceId,
    discountCodes,
    setDiscountCodes,
    createVenueDiscountCode,
    deleteVenueDiscountCode,
    refreshDiscountCodes,
    flatPricing,
    setFlatPricing,
    bookingRules,
    setBookingRules,
    operatingHoursPerPerson,
    setOperatingHoursPerPerson,
    accountSettings,
    setAccountSettings,
    ownerAccountSettings,
    setOwnerAccountSettings,
    busyTimes,
    fetchBusyTimes
  };
};

